import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ShoppingBag, Shield, Navigation, Monitor, Lock,
  Copy, Check, CheckCircle, Layout, BarChart3,
  Users, HeartPulse, RefreshCw, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsShopifyEmbeddedApp() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Shopify Embedded App
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Shopify Embedded App
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Access the full Commerce Index experience directly inside your Shopify Admin — no tab-switching required.
          The embedded app runs as an iframe within Shopify, authenticated via session tokens.
        </p>
      </div>

      {/* What is the Embedded App */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          What is the Embedded App?
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              The Commerce Index Shopify Embedded App runs inside the Shopify Admin as an iframe.
              When merchants install the app from the Shopify App Store, it appears in their
              Shopify Admin sidebar alongside their other apps. The entire CI dashboard, deal
              management, agent controls, and health scoring are available without leaving Shopify.
            </p>
            <div className="bg-[#0B1120] border border-[#22304A] rounded-lg p-4 mb-4">
              <p className="text-sm text-[#64748B] mb-2">How it works:</p>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7]">1.</span>
                  Merchant installs Commerce Index from the Shopify App Store
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7]">2.</span>
                  Shopify loads CI inside an iframe in the Admin panel
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7]">3.</span>
                  Shopify App Bridge provides session tokens for authentication
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7]">4.</span>
                  CI validates the token and renders the full dashboard experience
                </li>
              </ul>
            </div>
            <p className="text-[#94A3B8] text-sm">
              The embedded app uses <span className="text-white font-medium">Shopify App Bridge</span> to
              communicate with the host Shopify Admin. This enables native navigation, toast notifications,
              and modal dialogs that feel like part of Shopify itself.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Session Token Auth */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Session Token Authentication
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              Instead of cookies or OAuth redirects, the embedded app uses Shopify session tokens.
              These are short-lived JWTs provided by Shopify App Bridge, signed with your app's
              secret key. CI validates every request using HMAC verification.
            </p>
            <div className="relative">
              <button
                onClick={() => copyToClipboard(`// Frontend: Get session token from Shopify App Bridge
import { getSessionToken } from "@shopify/app-bridge-utils";

const token = await getSessionToken(app);

// Send authenticated request to CI backend
const response = await fetch("/api/ci/dashboard", {
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  }
});`, 'session-token')}
                className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors"
              >
                {copiedCode === 'session-token' ? (
                  <Check className="w-4 h-4 text-[#3EF1C7]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#64748B]" />
                )}
              </button>
              <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`// Frontend: Get session token from Shopify App Bridge
import { getSessionToken } from "@shopify/app-bridge-utils";

const token = await getSessionToken(app);

// Send authenticated request to CI backend
const response = await fetch("/api/ci/dashboard", {
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  }
});`}
              </pre>
            </div>
            <div className="relative mt-4">
              <button
                onClick={() => copyToClipboard(`# Backend: Validate session token via HMAC
import hmac
import hashlib
import base64
import json

def verify_shopify_session_token(token, api_secret):
    """Validate Shopify session token using HMAC-SHA256."""
    parts = token.split(".")
    if len(parts) != 3:
        raise ValueError("Invalid token format")

    header_payload = f"{parts[0]}.{parts[1]}"
    signature = parts[2]

    expected_sig = base64.urlsafe_b64encode(
        hmac.new(
            api_secret.encode(),
            header_payload.encode(),
            hashlib.sha256
        ).digest()
    ).rstrip(b"=").decode()

    if not hmac.compare_digest(signature, expected_sig):
        raise ValueError("Invalid token signature")

    payload = json.loads(base64.urlsafe_b64decode(parts[1] + "=="))
    return payload  # Contains shop, sub, exp, etc.`, 'hmac-verify')}
                className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors"
              >
                {copiedCode === 'hmac-verify' ? (
                  <Check className="w-4 h-4 text-[#3EF1C7]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#64748B]" />
                )}
              </button>
              <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`# Backend: Validate session token via HMAC
import hmac
import hashlib
import base64
import json

def verify_shopify_session_token(token, api_secret):
    """Validate Shopify session token using HMAC-SHA256."""
    parts = token.split(".")
    if len(parts) != 3:
        raise ValueError("Invalid token format")

    header_payload = f"{parts[0]}.{parts[1]}"
    signature = parts[2]

    expected_sig = base64.urlsafe_b64encode(
        hmac.new(
            api_secret.encode(),
            header_payload.encode(),
            hashlib.sha256
        ).digest()
    ).rstrip(b"=").decode()

    if not hmac.compare_digest(signature, expected_sig):
        raise ValueError("Invalid token signature")

    payload = json.loads(base64.urlsafe_b64decode(parts[1] + "=="))
    return payload  # Contains shop, sub, exp, etc.`}
              </pre>
            </div>
            <div className="mt-4 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
              <p className="text-sm text-[#64748B]">
                <strong className="text-white">Token payload includes:</strong> shop domain, user ID (sub),
                issuer (iss), destination, audience (aud), expiration (exp), issued-at (iat), and a unique
                session ID (sid). Tokens expire after 1 minute and are automatically refreshed by App Bridge.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Navigation */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Navigation Inside Shopify
        </h2>
        <p className="text-[#94A3B8] mb-6">
          The embedded app provides full navigation to all CI features directly within the Shopify Admin.
          Use the sidebar nav or top-level tabs to move between sections.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Layout className="w-6 h-6 text-[#3EF1C7] mb-2" />
              <CardTitle className="text-white">Dashboard</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Overview of your store health, recent activity, revenue metrics, and agent performance
                at a glance. The default landing page when opening the app.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <BarChart3 className="w-6 h-6 text-sky-400 mb-2" />
              <CardTitle className="text-white">Deals</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Create, manage, and monitor pricing deals. View active promotions, margin analysis,
                and deal performance metrics synced with your Shopify products.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Users className="w-6 h-6 text-amber-400 mb-2" />
              <CardTitle className="text-white">Agents</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Enable and configure AI agents. Monitor agent decisions, adjust trust tiers,
                and review the approval queue — all without leaving Shopify.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <HeartPulse className="w-6 h-6 text-rose-400 mb-2" />
              <CardTitle className="text-white">Health Score</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Real-time CI Health Score tracking. View component breakdowns, trend charts, and
                actionable recommendations to improve your store operations.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Available */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Features Available in the Embedded App
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <RefreshCw className="w-6 h-6 text-[#3EF1C7] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Product Sync</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Automatic two-way synchronization with your Shopify product catalog. Products, variants,
                    prices, and inventory levels are kept in sync via webhooks. Initial full sync runs on
                    first connection, then webhook-driven updates keep everything current.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <BarChart3 className="w-6 h-6 text-sky-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Deal Management</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Create and manage pricing deals tied to your Shopify products. Set deal parameters,
                    track performance metrics, and let agents optimize pricing within your defined guardrails.
                    Changes sync back to Shopify discount codes automatically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Users className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Agent Control</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Enable, disable, and configure AI agents from within the embedded app. Review agent
                    decisions in the approval queue, adjust trust tiers, set confidence thresholds, and
                    toggle between approval modes (auto, manual, hybrid).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <HeartPulse className="w-6 h-6 text-rose-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-2">Health Monitoring</h3>
                  <p className="text-[#94A3B8] text-sm">
                    View your CI Health Score, component breakdowns (fulfillment, communication, consistency,
                    policy adherence), and trend analysis. Get actionable recommendations to improve
                    your score and set up alerts for significant score changes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* GDPR Compliance */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          GDPR Compliance
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-6 h-6 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
              <p className="text-[#94A3B8]">
                Shopify requires all apps to implement mandatory GDPR webhooks. Commerce Index handles
                all three compliance endpoints automatically. These webhooks are registered during app
                installation and processed asynchronously.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-white font-semibold mb-2">customers/redact</h3>
                <p className="text-[#94A3B8] text-sm mb-2">
                  Fired when a merchant requests deletion of a customer's data, or 6 months after a
                  customer requests their data be deleted from a store. CI purges all associated
                  customer data from its systems.
                </p>
                <div className="relative">
                  <button
                    onClick={() => copyToClipboard(`POST /api/webhooks/shopify/customers-redact
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com",
  "customer": {
    "id": 67890,
    "email": "customer@example.com",
    "phone": "+1234567890"
  },
  "orders_to_redact": [12345, 67890]
}`, 'customers-redact')}
                    className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors"
                  >
                    {copiedCode === 'customers-redact' ? (
                      <Check className="w-4 h-4 text-[#3EF1C7]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#64748B]" />
                    )}
                  </button>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`POST /api/webhooks/shopify/customers-redact
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com",
  "customer": {
    "id": 67890,
    "email": "customer@example.com",
    "phone": "+1234567890"
  },
  "orders_to_redact": [12345, 67890]
}`}
                  </pre>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">shop/redact</h3>
                <p className="text-[#94A3B8] text-sm mb-2">
                  Fired 48 hours after a merchant uninstalls the app. CI deletes all shop data,
                  configuration, agent history, and synced products associated with the store.
                </p>
                <div className="relative">
                  <button
                    onClick={() => copyToClipboard(`POST /api/webhooks/shopify/shop-redact
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com"
}`, 'shop-redact')}
                    className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors"
                  >
                    {copiedCode === 'shop-redact' ? (
                      <Check className="w-4 h-4 text-[#3EF1C7]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#64748B]" />
                    )}
                  </button>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`POST /api/webhooks/shopify/shop-redact
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com"
}`}
                  </pre>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">customers/data_request</h3>
                <p className="text-[#94A3B8] text-sm mb-2">
                  Fired when a customer requests their data from a store. CI compiles all stored
                  data for the customer and makes it available for the merchant to provide.
                </p>
                <div className="relative">
                  <button
                    onClick={() => copyToClipboard(`POST /api/webhooks/shopify/customers-data-request
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com",
  "customer": {
    "id": 67890,
    "email": "customer@example.com",
    "phone": "+1234567890"
  },
  "orders_requested": [12345, 67890]
}`, 'data-request')}
                    className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors"
                  >
                    {copiedCode === 'data-request' ? (
                      <Check className="w-4 h-4 text-[#3EF1C7]" />
                    ) : (
                      <Copy className="w-4 h-4 text-[#64748B]" />
                    )}
                  </button>
                  <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
{`POST /api/webhooks/shopify/customers-data-request
Content-Type: application/json
X-Shopify-Hmac-SHA256: <hmac>

{
  "shop_id": 12345,
  "shop_domain": "mystore.myshopify.com",
  "customer": {
    "id": 67890,
    "email": "customer@example.com",
    "phone": "+1234567890"
  },
  "orders_requested": [12345, 67890]
}`}
                  </pre>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#64748B]">
                  All GDPR webhooks are verified via HMAC-SHA256 before processing. Unverified
                  requests are rejected with a 401 status. Redaction jobs run asynchronously and
                  are confirmed within 24 hours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/shopify-sync">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Shopify Sync →</h3>
                <p className="text-sm text-[#94A3B8]">Learn how product and inventory sync works</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/channel-setup">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Channel Setup →</h3>
                <p className="text-sm text-[#94A3B8]">Set up messaging channels for your store</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
